import { defineComponent, reactive, computed, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { withLoading } from '@hems/component';
import { UserReportService, HomeApplianceService } from '@hems/service';
import { getChartByApplianceOptions } from '@hems/user/src/containers/homeAppliance/applianceChartOptions';
import { Helper, DateHelper } from '@hems/util';
import { GraphChart } from '@/components';
import { APPLIANCE_TYPE } from '@/config/constants';
import { getDateInitialValue, getNowByClient } from '@/containers/report/chartFunctions';
export default defineComponent({
    name: 'ApplianceChartByDeviceContainer',
    components: {
        GraphChart,
    },
    props: {
        data: {
            type: Object,
            default: null,
        },
    },
    setup(props) {
        const { t } = useI18n();
        const store = useStore();
        const userReportService = new UserReportService(window.axiosInstance.axios);
        const homeApplianceService = new HomeApplianceService(window.axiosInstance.axios);
        const state = reactive({
            applianceDetails: computed(() => props.data),
            show: false,
            tabname: 'daily',
            locale: computed(() => store.state.appCtx.locale),
            date: null,
            baseDeviceInfo: computed(() => store.state.device.baseDeviceInfo),
            siteId: computed(() => Number(store.state.user.homeId)),
            chartName: computed(() => props.data.profile_cd === APPLIANCE_TYPE.HEAT_PUMP
                ? 'Heat Pump'
                : props.data.profile_cd === APPLIANCE_TYPE.AIR_CONDITIONER
                    ? 'Air Conditioner'
                    : props.data.profile_cd === APPLIANCE_TYPE.SMART_PLUG
                        ? 'Smart Plug'
                        : props.data.profile_cd === APPLIANCE_TYPE.TV
                            ? 'TV'
                            : ''),
            data: {
                heatPumpData: [],
                airConditionerData: [],
                smartPlugData: [],
                tvData: [],
            },
            todayEnergyConsumption: Helper.formatNumber(0, 1),
        });
        watch(() => state.applianceDetails, () => {
            getServerTime();
        });
        watch(() => state.date, () => {
            if (!state.date)
                return;
            loadData();
        }, {
            deep: true,
        });
        watch(() => state.locale, () => {
            rerenderData();
        });
        function toDateFormatString(date, interval) {
            const year = date.getFullYear();
            const month = Helper.padLeft(date.getMonth() + 1, 2);
            const day = Helper.padLeft(date.getDate(), 2);
            return interval === 'monthly' ? [year, month].join('') : [year, month, day].join('');
        }
        function getDateParams() {
            if (state.tabname === 'hourly') {
                return {
                    from: `${toDateFormatString(state.date?.hourly, state.tabname)}00`,
                    to: `${toDateFormatString(state.date?.hourly, state.tabname)}23`,
                };
            }
            if (state.tabname === 'daily') {
                return {
                    from: toDateFormatString(state.date?.daily.start, state.tabname),
                    to: toDateFormatString(state.date?.daily.end, state.tabname),
                };
            }
            return {
                from: toDateFormatString(state.date?.monthly.start, state.tabname),
                to: toDateFormatString(state.date?.monthly.end, state.tabname),
            };
        }
        const options = computed(() => getChartByApplianceOptions(state.tabname, state.locale, props.data.profile_cd === APPLIANCE_TYPE.HEAT_PUMP
            ? state.data.heatPumpData
            : props.data.profile_cd === APPLIANCE_TYPE.AIR_CONDITIONER
                ? state.data.airConditionerData
                : props.data.profile_cd === APPLIANCE_TYPE.SMART_PLUG
                    ? state.data.smartPlugData
                    : state.data.tvData, state.chartName, t));
        function createChartData(data) {
            const todayTime = state.date?.hourly.getTime();
            state.data.heatPumpData = data.HeatPump.map((item) => ({
                x: DateHelper.getTime(item.colec_dt, state.tabname),
                y: item.gen || 0,
                color: todayTime === DateHelper.getTime(item.colec_dt, state.tabname) ? '#00c6c1' : undefined,
            }));
            state.data.airConditionerData = data['Air Conditioner'].map((item) => ({
                x: DateHelper.getTime(item.colec_dt, state.tabname),
                y: item.gen || 0,
                color: todayTime === DateHelper.getTime(item.colec_dt, state.tabname) ? '#00c6c1' : undefined,
            }));
            state.data.smartPlugData = data['Smart Plug'].map((item) => ({
                x: DateHelper.getTime(item.colec_dt, state.tabname),
                y: item.gen || 0,
                color: todayTime === DateHelper.getTime(item.colec_dt, state.tabname) ? '#00c6c1' : undefined,
            }));
            state.data.tvData = data.TV.map((item) => ({
                x: DateHelper.getTime(item.colec_dt, state.tabname),
                y: item.gen || 0,
                color: todayTime === DateHelper.getTime(item.colec_dt, state.tabname) ? '#00c6c1' : undefined,
            }));
            state.todayEnergyConsumption =
                props.data.profile_cd === APPLIANCE_TYPE.HEAT_PUMP
                    ? Helper.formatNumber(state.data.heatPumpData[state.data.heatPumpData.length - 1].y, 2)
                    : props.data.profile_cd === APPLIANCE_TYPE.AIR_CONDITIONER
                        ? Helper.formatNumber(state.data.airConditionerData[state.data.airConditionerData.length - 1].y, 2)
                        : props.data.profile_cd === APPLIANCE_TYPE.SMART_PLUG
                            ? Helper.formatNumber(state.data.smartPlugData[state.data.smartPlugData.length - 1].y, 2)
                            : Helper.formatNumber(state.data.tvData[state.data.tvData.length - 1].y, 2);
        }
        const getServerTime = () => {
            userReportService
                .getServerTime(state.baseDeviceInfo.timezone_id)
                .then(async (res) => {
                state.date = getDateInitialValue(res);
            })
                .catch(async (e) => {
                console.error(e);
                state.date = getDateInitialValue(getNowByClient(state.baseDeviceInfo.timezone_id));
            });
        };
        async function loadData() {
            if (state.date === null)
                return;
            await withLoading(async () => {
                await homeApplianceService
                    .getChartByAppliance(String(state.applianceDetails.device_id), {
                    interval: state.tabname,
                    ...getDateParams(),
                    timezoneId: state.baseDeviceInfo.timezone_id,
                    profileCode: state.applianceDetails.profile_cd,
                })
                    .then((res) => {
                    createChartData(res);
                })
                    .catch((e) => {
                    console.error(e);
                    state.data.heatPumpData = [];
                    state.data.airConditionerData = [];
                    state.data.smartPlugData = [];
                    state.data.tvData = [];
                });
            })();
            nextTick(() => (state.show = true));
        }
        getServerTime();
        function rerenderData() {
            state.show = false;
            nextTick(() => (state.show = true));
        }
        return {
            state,
            options,
        };
    },
});
